export type FieldModeEvent = 'field_action_click' | 'map_route_open'

export type FieldModeLink = {
  action: string
  event: FieldModeEvent
  external?: boolean
  href: string
  label: string
}

export type FieldModeSource = {
  href: string
  label: string
}

export type GuideFieldModeConfig = {
  backup: string
  backupLink?: FieldModeLink
  checklist: string[]
  fastAnswer: string
  primaryAction: FieldModeLink
  sources: FieldModeSource[]
  verifiedOn: string
}

export const GUIDE_FIELD_MODE: Record<string, GuideFieldModeConfig> = {
  'where-to-charge-your-phone-in-manhattan-guide': {
    fastAnswer:
      'Below about 15% battery, stop searching cafés and use a working LinkNYC kiosk with your own cable. Keep a physical contactless card as the transit backup if the phone dies.',
    verifiedOn: '2026-09-09',
    primaryAction: {
      action: 'find-linknyc',
      event: 'map_route_open',
      external: true,
      href: 'https://www.link.nyc/find-a-link.html',
      label: 'Find a LinkNYC kiosk',
    },
    backup:
      'If street charging is impractical, use a staffed indoor fallback and preserve the remaining battery for navigation, tickets, and calls.',
    checklist: [
      'Bring your own charging cable.',
      'Lower brightness and save the next address offline.',
      'Keep a physical contactless card separate from the phone for OMNY.',
      'If battery drain keeps repeating, buy or carry your own power bank.',
    ],
    sources: [
      {
        href: 'https://www.link.nyc/faq.html',
        label: 'LinkNYC charging FAQ',
      },
    ],
  },
  'manhattan-luggage-storage-guide': {
    fastAnswer:
      'Penn/Moynihan currently has Amtrak temporary bag storage; Grand Central does not. Store near where you need the bag back, not simply where your morning starts.',
    verifiedOn: '2026-09-08',
    primaryAction: {
      action: 'open-hub-matrix',
      event: 'field_action_click',
      href: '#transit-hub-storage',
      label: 'Compare storage by transit hub',
    },
    backup:
      'If official hub storage does not fit, use your own hotel only when retrieval stays on your route; otherwise choose a verified off-site option near the final departure path.',
    backupLink: {
      action: 'checkout-flight-backup',
      event: 'field_action_click',
      href: '/guides/what-to-do-between-hotel-checkout-and-an-evening-flight-guide',
      label: 'Plan checkout → evening flight',
    },
    checklist: [
      'Decide where you need the bag back before booking storage.',
      'Keep passports, medication, keys, and difficult-to-replace valuables with you.',
      'Screenshot the exact address, claim details, and pickup deadline.',
      'Leave a retrieval buffer before a train, bus, or airport trip.',
    ],
    sources: [
      {
        href: 'https://www.amtrak.com/baggage-services',
        label: 'Amtrak baggage services',
      },
      {
        href: 'https://grandcentralterminal.com/about/',
        label: 'Grand Central facilities FAQ',
      },
    ],
  },
  'late-night-pharmacies-in-manhattan': {
    fastAnswer:
      'Check the pharmacy-counter hours for the exact address, not only the front-store hours. If a prescription is time-critical, call the pharmacy department before crossing Manhattan.',
    verifiedOn: '2026-09-09',
    primaryAction: {
      action: 'open-24h-pharmacy-locator',
      event: 'field_action_click',
      external: true,
      href: 'https://www.cvs.com/store-locator/cvs-pharmacy-locations/New-York/New-York/24-hour-pharmacies',
      label: 'Check current 24-hour pharmacies',
    },
    backup:
      'If medication or symptoms cannot safely wait for a pharmacy counter, switch from retail-pharmacy hunting to the appropriate same-day medical-care option.',
    backupLink: {
      action: 'same-day-care-backup',
      event: 'field_action_click',
      href: '/guides/same-day-medical-care-in-manhattan-guide',
      label: 'Open same-day care options',
    },
    checklist: [
      'Verify the exact street address.',
      'Check prescription-counter hours separately from store hours.',
      'Call about the medication or pharmacist service you actually need.',
      'Keep an urgent-care backup if the need cannot wait.',
    ],
    sources: [
      {
        href: 'https://www.cvs.com/store-locator/cvs-pharmacy-locations/New-York/New-York/24-hour-pharmacies',
        label: 'CVS 24-hour pharmacy locator',
      },
    ],
  },
  'the-manhattan-subway-for-first-time-visitors': {
    fastAnswer:
      'The subway/local-bus fare is currently $3 for most riders. Tap the same contactless card, phone/watch, or OMNY card, confirm Uptown/Downtown plus local/express, and check service changes before entering.',
    verifiedOn: '2026-09-09',
    primaryAction: {
      action: 'open-mta-alerts',
      event: 'field_action_click',
      external: true,
      href: 'https://www.mta.info/alerts',
      label: 'Check current MTA service changes',
    },
    backup:
      'If stairs, elevator status, luggage, or a short cross-town trip makes the subway the wrong tool, switch to walking, bus, or a licensed taxi rather than forcing the route.',
    backupLink: {
      action: 'accessibility-backup',
      event: 'field_action_click',
      href: '/guides/manhattan-when-stairs-and-crowds-are-the-real-obstacle',
      label: 'Open stairs & accessibility backup',
    },
    checklist: [
      'Confirm Uptown vs. Downtown before entering.',
      'Check whether the train is local or express.',
      'Use the same payment method if you want fare-capping progress to follow your rides.',
      'Check MTA alerts for planned service changes.',
    ],
    sources: [
      {
        href: 'https://www.mta.info/fares-tolls',
        label: 'MTA fares and tolls',
      },
      {
        href: 'https://www.mta.info/alerts',
        label: 'MTA planned service changes',
      },
    ],
  },
  'getting-from-jfk-laguardia-or-newark-to-manhattan': {
    fastAnswer:
      'Choose by airport, luggage, mobility, and final Manhattan stop. Current public-transit anchors include JFK AirTrain + subway at $11.75 and EWR rail at $17.25; check live advisories before leaving.',
    verifiedOn: '2026-09-09',
    primaryAction: {
      action: 'open-mta-trip-planner',
      event: 'field_action_click',
      external: true,
      href: 'https://www.mta.info/tripplanner',
      label: 'Open the MTA trip planner',
    },
    backup:
      'If rail/bus transfers are a poor fit for heavy luggage, mobility, or the arrival hour, compare a licensed taxi or current rideshare quote instead of forcing public transit.',
    checklist: [
      'Confirm the airport terminal and your Manhattan destination.',
      'Check live construction/service advisories.',
      'Compare whole-party cost when traveling as a group.',
      'Keep extra transfer time when an airport rail connection is under construction.',
    ],
    sources: [
      {
        href: 'https://www.jfkairport.com/transportation/airtrain',
        label: 'JFK AirTrain',
      },
      {
        href: 'https://www.njtransit.com/ewr',
        label: 'NJ TRANSIT EWR guide',
      },
    ],
  },
  'public-restrooms-in-manhattan-reliable-options-and-backup-plans': {
    fastAnswer:
      'Open NYC’s current restroom locator before a long walking segment. The city lists public restrooms in parks, libraries, POPS, and transit facilities; hours and accessibility vary by exact location.',
    verifiedOn: '2026-09-09',
    primaryAction: {
      action: 'open-nyc-restroom-finder',
      event: 'map_route_open',
      external: true,
      href: 'https://portal.311.nyc.gov/article/?kanumber=KA-03643',
      label: 'Open the NYC public-restroom finder',
    },
    backup:
      'If the nearest listed facility is closed, use the next open library, park, POPS, transit hub, museum, or other staffed venue instead of waiting for an emergency.',
    checklist: [
      'Check the exact facility hours before walking there.',
      'Check accessibility details if step-free access matters.',
      'Use a restroom before starting a long outdoor segment.',
      'Treat private café restroom rules as venue-specific.',
    ],
    sources: [
      {
        href: 'https://portal.311.nyc.gov/article/?kanumber=KA-03643',
        label: 'NYC311 Public Restrooms',
      },
    ],
  },
}

export const FIELD_MODE_SLUGS = Object.keys(GUIDE_FIELD_MODE)
